import { useMemo } from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, Check, Mail } from 'lucide-react';
import {
  Bar,
  BarChart,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useOrgStore } from '../store/useOrgStore';
import { StatCard } from '../components/ui/StatCard';
import { EmptyState } from '../components/ui/EmptyState';
import { RiskBadge } from '../components/ui/RiskBadge';
import { CollapsibleSection } from '../components/ui/CollapsibleSection';
import type { DeliverabilityLevel, EmailResult, EmailValidityBuckets } from '../types/org.types';

const COLORS = {
  valid: '#3FA96B',
  freeDomain: '#9A988F',
  invalid: '#C7851A',
  missing: '#D64545',
};

function combineBuckets(
  a: EmailValidityBuckets | null,
  b: EmailValidityBuckets | null,
): { name: string; value: number; fill: string }[] {
  const sum = (k: keyof EmailValidityBuckets) => (a?.[k] ?? 0) + (b?.[k] ?? 0);
  return [
    { name: 'Valid (business)', value: sum('valid'), fill: COLORS.valid },
    { name: 'Free domain', value: sum('freeDomain'), fill: COLORS.freeDomain },
    { name: 'Invalid format', value: sum('invalid'), fill: COLORS.invalid },
    { name: 'Missing', value: sum('missing'), fill: COLORS.missing },
  ];
}

function deliverabilityLabel(level: DeliverabilityLevel): string {
  if (level === 'AllEmail') return 'All Email';
  if (level === 'SystemEmailOnly') return 'System Email Only';
  if (level === 'NoAccess') return 'No Access';
  return 'Unknown';
}

// Live audit store by default, which since TYR-216 is the client Hub alone;
// a stored client scan passes its own audit in.
// Passing `data` at all, null included, is scan mode: the store is not read.
export function EmailDeliverabilityPage({ data }: { data?: EmailResult | null } = {}) {
  const storeEmail = useOrgStore((s) => s.audit.email);
  const scanMode = data !== undefined;
  const email = scanMode ? data : storeEmail;

  const validityData = useMemo(
    () => (email ? combineBuckets(email.validity.contacts, email.validity.leads) : []),
    [email],
  );
  const validityTotal = validityData.reduce((acc, d) => acc + d.value, 0);

  const optOutCombined = useMemo(() => {
    if (!email) return null;
    const c = email.optOut.contacts;
    const l = email.optOut.leads;
    if (!c && !l) return null;
    const total = (c?.total ?? 0) + (l?.total ?? 0);
    const optedOut = (c?.optedOut ?? 0) + (l?.optedOut ?? 0);
    return { total, optedOut, rate: total > 0 ? (optedOut / total) * 100 : 0 };
  }, [email]);

  if (!email) return <EmptyState title="Email data not loaded" hint="Reconnect to scan again." icon={<Mail size={20} />} />;

  const optOutTone = !optOutCombined
    ? 'adopt'
    : optOutCombined.rate > 15
      ? 'sunset'
      : optOutCombined.rate > 5
        ? 'sunrise'
        : 'grow';

  return (
    <div className="space-y-8">
      <div>
        <div className="text-xs uppercase tracking-widest text-sunset mb-1">Email</div>
        <h1 className="text-3xl font-semibold text-crux-light tracking-tight">Email Intelligence</h1>
        <p className="text-sm text-neutral-500 mt-1 max-w-3xl">
          Email validity, opt-out exposure, duplication, and deliverability settings, the signals
          that decide whether the org's outbound email actually lands. Every gap here resolves with
          Marketing Cloud or Data Cloud.
        </p>
      </div>

      {email.deliverabilitySetting?.warning && (
        <Banner tone="critical" icon={<AlertTriangle size={16} />}>
          <strong>Deliverability setting: {deliverabilityLabel(email.deliverabilitySetting.level)}.</strong>{' '}
          {email.deliverabilitySetting.warning}
        </Banner>
      )}

      <motion.div
        initial="hidden"
        animate="visible"
        variants={{ visible: { transition: { staggerChildren: 0.05 } } }}
        className="grid grid-cols-2 sm:grid-cols-4 gap-3"
      >
        {[
          <StatCard
            key="total"
            label="Sample size"
            value={validityTotal.toLocaleString()}
            hint="Top 5,000 of each by recency"
            accent="adopt"
          />,
          <StatCard
            key="opt-out"
            label="Opt-out rate"
            value={optOutCombined ? `${optOutCombined.rate.toFixed(1)}%` : '-'}
            hint={optOutCombined ? `${optOutCombined.optedOut.toLocaleString()} of ${optOutCombined.total.toLocaleString()}` : undefined}
            accent={optOutTone}
          />,
          <StatCard
            key="dupes"
            label="Duplicate emails"
            value={email.duplicates ? email.duplicates.duplicateGroups.toLocaleString() : '-'}
            hint={email.duplicates ? `${email.duplicates.duplicateRecords} records affected` : undefined}
            accent={email.duplicates && email.duplicates.duplicateGroups > 0 ? 'sunset' : 'grow'}
          />,
          <StatCard
            key="overlap"
            label="Lead↔Contact overlap"
            value={email.overlap ? email.overlap.overlapCount.toLocaleString() : '-'}
            hint={email.overlap ? `${email.overlap.overlapPct.toFixed(1)}% of leads` : undefined}
            accent={email.overlap && email.overlap.overlapCount > 0 ? 'sunset' : 'grow'}
          />,
        ].map((c, i) => (
          <motion.div
            key={i}
            variants={{
              hidden: { opacity: 0, y: 6 },
              visible: { opacity: 1, y: 0, transition: { duration: 0.25 } },
            }}
          >
            {c}
          </motion.div>
        ))}
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-surface-1 border border-border rounded-lg p-6">
          <div className="text-[11px] uppercase tracking-widest text-neutral-500 mb-4">
            Email Validity (Contacts + Leads)
          </div>
          {validityTotal > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={validityData} dataKey="value" nameKey="name" innerRadius={60} outerRadius={100} paddingAngle={2}>
                  {validityData.map((entry) => (
                    <Cell key={entry.name} fill={entry.fill} stroke="#EEEDE9" />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ background: '#E7E5DF', border: '1px solid #DAD8D1', borderRadius: 0 }}
                  itemStyle={{ color: '#1B1B1B' }}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-sm text-neutral-500 py-6">No email data sampled.</div>
          )}
          <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
            {validityData.map((d) => (
              <div key={d.name} className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full" style={{ background: d.fill }} />
                <span className="text-neutral-400 flex-1">{d.name}</span>
                <span className="text-crux-light tabular-nums">{d.value.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-surface-1 border border-border rounded-lg p-6">
          <div className="text-[11px] uppercase tracking-widest text-neutral-500 mb-4">
            Email Activity (last 12 months)
          </div>
          {email.activity && email.activity.byMonth.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={email.activity.byMonth}>
                <XAxis dataKey="month" tick={{ fill: '#6b6b66', fontSize: 10 }} stroke="#DAD8D1" />
                <YAxis tick={{ fill: '#6b6b66', fontSize: 10 }} stroke="#DAD8D1" />
                <Tooltip
                  contentStyle={{ background: '#E7E5DF', border: '1px solid #DAD8D1', borderRadius: 0 }}
                  itemStyle={{ color: '#1B1B1B' }}
                />
                <Bar dataKey="count" fill="#5B7CFF" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-sm text-neutral-500 py-6">
              {email.activity?.last90Days === 0
                ? 'No EmailMessage records in the last 90 days. The org may have moved to an external tool.'
                : 'No email activity data.'}
            </div>
          )}
          <div className="mt-3 text-xs text-neutral-500">
            {email.activity ? `${email.activity.last90Days.toLocaleString()} EmailMessages in last 90 days` : ''}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-surface-1 border border-border rounded-lg p-6">
          <div className="text-[11px] uppercase tracking-widest text-neutral-500 mb-4">
            Email-to-Case Health
          </div>
          {email.emailToCase ? (
            <ul className="space-y-2 text-sm">
              <ChecklistRow ok={email.emailToCase.routingAddresses > 0} label={`${email.emailToCase.routingAddresses} routing address${email.emailToCase.routingAddresses === 1 ? '' : 'es'} configured`} />
              <ChecklistRow ok={email.emailToCase.activeRoutingAddresses > 0} label={`${email.emailToCase.activeRoutingAddresses} active`} />
              <ChecklistRow
                ok={email.emailToCase.lightningEditorMigrated === 'yes'}
                label={`Lightning editor migration · ${email.emailToCase.lightningEditorMigrated}`}
              />
            </ul>
          ) : (
            <div className="text-sm text-neutral-500">EmailServicesAddress not queryable in this org.</div>
          )}
        </div>

        <div className="bg-surface-1 border border-border rounded-lg p-6">
          <div className="text-[11px] uppercase tracking-widest text-neutral-500 mb-4">
            Template Inventory
          </div>
          {email.templates ? (
            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-3">
                <span className="text-neutral-500 w-24">Lightning</span>
                <div className="flex-1 h-2 bg-surface-3 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-grow"
                    style={{
                      width: `${email.templates.total > 0 ? (email.templates.lightningCount / email.templates.total) * 100 : 0}%`,
                    }}
                  />
                </div>
                <span className="text-crux-light tabular-nums w-12 text-right">{email.templates.lightningCount}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-neutral-500 w-24">Classic</span>
                <div className="flex-1 h-2 bg-surface-3 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-sunrise"
                    style={{
                      width: `${email.templates.total > 0 ? (email.templates.classicCount / email.templates.total) * 100 : 0}%`,
                    }}
                  />
                </div>
                <span className="text-crux-light tabular-nums w-12 text-right">{email.templates.classicCount}</span>
              </div>
              <div className="text-xs text-neutral-500 pt-1">
                {email.templates.total} total templates ·{' '}
                {email.templates.classicCount > 0 && (
                  <span className="text-sunrise">Classic templates won't render in modern Flows</span>
                )}
              </div>
            </div>
          ) : (
            <div className="text-sm text-neutral-500">EmailTemplate not queryable.</div>
          )}
        </div>
      </div>

      {email.duplicates && email.duplicates.topDuplicates.length > 0 && (
        <CollapsibleSection
          title="Top duplicate emails"
          meta={`${email.duplicates.topDuplicates.length} shown`}
          defaultOpen={false}
        >
          <div className="border border-border rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-surface-2">
                <tr className="text-[11px] uppercase tracking-widest text-neutral-500">
                  <th className="text-left px-4 py-3 font-medium">Email</th>
                  <th className="text-right px-4 py-3 font-medium">Records sharing it</th>
                  <th className="text-left px-4 py-3 font-medium">Risk</th>
                </tr>
              </thead>
              <tbody>
                {email.duplicates.topDuplicates.map((d, i) => (
                  <tr key={d.email} className={i % 2 === 0 ? 'bg-surface-1/40' : 'bg-transparent'}>
                    <td className="px-4 py-2.5 text-crux-light font-mono text-xs">{d.email}</td>
                    <td className="px-4 py-2.5 text-right text-neutral-300 tabular-nums">{d.count}</td>
                    <td className="px-4 py-2.5">
                      <RiskBadge tone={d.count > 5 ? 'critical' : 'medium'}>
                        {d.count > 5 ? 'High' : 'Medium'}
                      </RiskBadge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CollapsibleSection>
      )}
    </div>
  );
}

function ChecklistRow({ ok, label }: { ok: boolean; label: string }) {
  return (
    <li className="flex items-center gap-2">
      <span
        className={`w-4 h-4 rounded-full flex items-center justify-center ${
          ok ? 'bg-grow/20 text-grow' : 'bg-sunset/20 text-sunset'
        }`}
      >
        {ok ? <Check size={10} /> : <AlertTriangle size={10} />}
      </span>
      <span className="text-crux-light">{label}</span>
    </li>
  );
}

function Banner({
  tone,
  icon,
  children,
}: {
  tone: 'critical' | 'warning' | 'success';
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  const cls =
    tone === 'critical'
      ? 'bg-sunset/10 border-sunset/30 text-sunset'
      : tone === 'success'
        ? 'bg-grow/10 border-grow/30 text-grow'
        : 'bg-sunrise/10 border-sunrise/30 text-sunrise';
  return (
    <div className={`flex items-start gap-3 border rounded-md p-4 text-sm ${cls}`}>
      <span className="mt-0.5 shrink-0">{icon}</span>
      <span className="text-crux-light/90">{children}</span>
    </div>
  );
}
